if (!customElements.get('rr-music-previews')) {
  customElements.define('rr-music-previews', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.audio = this.querySelector('audio');
      this.status = this.querySelector('[data-preview-status]');
      this.queue = [];
      this.index = 0;
      this.request = 0;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-preview-urls]');
        if (button && this.contains(button) && !button.disabled) this.toggle(button);
      }, options);
      this.addEventListener('rr:previews-updated', () => this.update(), options);
      this.audio.addEventListener('playing', () => { this.pending = false; this.update(); }, options);
      this.audio.addEventListener('pause', () => { this.pending = false; this.update(); }, options);
      this.audio.addEventListener('waiting', () => { if (!this.audio.paused) this.pending = true; this.update(); }, options);
      this.audio.addEventListener('ended', () => {
        if (this.index + 1 < this.queue.length) { this.index++; this.start(); }
        else { this.index = 0; this.pending = false; this.audio.removeAttribute('src'); this.audio.load(); this.update(); }
      }, options);
      this.audio.addEventListener('error', () => {
        this.pending = false;
        this.status.textContent = this.dataset.errorLabel;
        this.update();
      }, options);
      document.addEventListener('play', event => {
        if (event.target === this.audio) {
          document.querySelectorAll('audio,video').forEach(media => { if (media !== this.audio) media.pause(); });
        } else { this.audio.pause(); }
      }, { ...options, capture: true });
      this.update();
    }
    disconnectedCallback() {
      this.request++;
      this.controller.abort();
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    urls(button) {
      try {
        const values = JSON.parse(button.dataset.previewUrls || '[]');
        if (!Array.isArray(values)) return [];
        return values.slice(0, 10).flatMap(value => {
          if (typeof value !== 'string') return [];
          const url = new URL(value, document.baseURI);
          const hosted = url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/files/'));
          return url.protocol === 'https:' && hosted && /\.(mp3|wav|m4a|aac|ogg)$/i.test(url.pathname) ? [url.href] : [];
        });
      } catch { return []; }
    }
    toggle(button) {
      const queue = this.urls(button);
      if (!queue.length) return;
      if (this.currentId === button.dataset.previewId && (this.pending || !this.audio.paused)) {
        this.request++;
        this.pending = false;
        this.audio.pause();
        this.update();
        return;
      }
      if (this.currentId !== button.dataset.previewId) {
        this.request++;
        this.audio.pause();
        this.currentId = button.dataset.previewId;
        this.queue = queue;
        this.index = 0;
        this.audio.removeAttribute('src');
      }
      this.status.textContent = '';
      this.start();
    }
    async start() {
      const request = ++this.request;
      this.pending = true;
      if (this.audio.src !== this.queue[this.index] || this.audio.error) {
        this.audio.src = this.queue[this.index];
        this.audio.load();
      }
      this.update();
      try {
        await this.audio.play();
        if (request !== this.request) return;
        this.pending = false;
        this.update();
      } catch (error) {
        if (request !== this.request) return;
        this.pending = false;
        this.update();
        if (error.name !== 'AbortError') this.status.textContent = this.dataset.errorLabel;
      }
    }
    update() {
      this.querySelectorAll('button[data-preview-urls]').forEach(button => {
        const available = this.urls(button).length > 0;
        const active = available && button.dataset.previewId === this.currentId;
        const playing = active && !this.audio.paused && !this.audio.ended;
        const loading = active && this.pending;
        const label = !available ? this.dataset.unavailableLabel : loading ? this.dataset.loadingLabel : playing ? this.dataset.pauseLabel : this.dataset.playLabel;
        button.disabled = !available;
        button.dataset.state = loading ? 'loading' : playing ? 'playing' : 'paused';
        button.setAttribute('aria-pressed', String(playing || loading));
        button.setAttribute('aria-busy', String(!!loading));
        button.setAttribute('aria-label', label + ': ' + button.dataset.previewTitle);
        button.title = label;
        const text = button.querySelector('[data-preview-label]');
        if (text) text.textContent = label;
      });
    }
  });
}

if (!customElements.get('rr-monthly-chart')) {
  customElements.define('rr-monthly-chart', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.resizeObserver = new ResizeObserver(() => this.fitLatest());
      this.resizeObserver.observe(this);
      this.window = '30d';
      this.request = 0;
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-chart-window]');
        if (!tab || tab.dataset.chartWindow === this.window) return;
        this.window = tab.dataset.chartWindow;
        this.querySelectorAll('[data-chart-window]').forEach(button => button.setAttribute('aria-pressed', String(button === tab)));
        this.rendered = false;
        this.load();
      }, { signal: this.controller.signal });
      this.load();
      this.timer = setInterval(() => { if (!document.hidden) this.load(); }, 900000);
    }
    disconnectedCallback() {
      this.controller.abort();
      this.resizeObserver.disconnect();
      clearInterval(this.timer);
    }
    fitLatest() {
      const section = this.closest('.rr-music');
      const latest = section?.querySelector('.rr-music__latest');
      if (!latest) { this.style.minHeight = ''; return; }
      latest.style.maxWidth = '';
      latest.style.paddingTop = '';
      this.style.minHeight = '';
      if (!matchMedia('(min-width: 990px)').matches || this.querySelectorAll('[data-chart-list] li').length < 5) return;
      const grid = section.querySelector('.rr-music__grid');
      const targetHeight = grid.getBoundingClientRect().bottom - this.getBoundingClientRect().top;
      this.style.minHeight = Math.ceil(targetHeight) + 'px';
    }
    async load() {
      const status = this.querySelector('[data-chart-status]');
      const list = this.querySelector('[data-chart-list]');
      const request = ++this.request;
      const period = this.window === 'all' ? 'all_time' : 'last_30_days';
      if (!this.rendered) { list.hidden = true; status.hidden = false; status.textContent = this.dataset.loading; }
      try {
        const endpoint = new URL(this.dataset.endpoint); endpoint.searchParams.set('window', this.window);
        const response = await fetch(endpoint, { credentials:'omit', signal:this.controller.signal });
        if (!response.ok) throw new Error('Chart unavailable');
        const chart = await response.json();
        if (request !== this.request) return;
        if (chart.period !== period || !Array.isArray(chart.entries)) throw new Error('Chart unavailable');
        const items = [];
        for (const track of chart.entries.slice(0, 10)) {
          if (!/^\/products\/[a-z0-9-]+$/.test(track.url) || typeof track.title !== 'string') continue;
          const li = document.createElement('li');
          const link = document.createElement('div'); link.className = 'rr-music__row';
          const rank = document.createElement('span'); rank.className = 'rr-music__rank'; rank.textContent = '#' + (items.length + 1); rank.setAttribute('aria-hidden', 'true');
          const image = document.createElement('img'); image.className = 'rr-music__thumbnail'; image.alt = ''; image.width = 48; image.height = 48; image.loading = 'lazy';
          if (track.image) { const url = new URL(track.image); if (url.protocol === 'https:' && url.hostname === 'cdn.shopify.com') { url.searchParams.set('width', '96'); image.src = url.href; } }
          const label = document.createElement('a'); label.className = 'rr-music__track'; label.href = track.url; label.title = track.title;
          const title = document.createElement('strong'); title.textContent = track.title;
          label.append(title);
          if (track.artist) { const artist = document.createElement('span'); artist.textContent = track.artist; label.append(artist); }
          const artwork = document.createElement('a'); artwork.href = track.url; artwork.setAttribute('aria-label', track.title); artwork.append(image);
          const play = document.createElement('button'); play.type = 'button'; play.className = 'rr-music__play rr-music__preview';
          play.dataset.previewUrls = JSON.stringify(Array.isArray(track.previews) ? track.previews : []);
          play.dataset.previewId = track.handle; play.dataset.previewTitle = track.title;
          play.setAttribute('aria-pressed', 'false'); play.setAttribute('aria-label', this.closest('rr-music-previews').dataset.playLabel + ': ' + track.title);
          play.disabled = !track.previews?.length;
          const icon = document.createElement('span'); icon.className = 'rr-music__play-icon'; icon.setAttribute('aria-hidden', 'true'); play.append(icon);
          link.append(rank, artwork, label, play); li.append(link); items.push(li);
        }
        list.replaceChildren(...items); list.hidden = items.length === 0;
        status.hidden = items.length > 0;
        status.textContent = chart.pending ? this.dataset.pending : this.dataset.empty;
        this.rendered = true;
        this.dispatchEvent(new CustomEvent('rr:previews-updated', { bubbles: true }));
        this.fitLatest();
      } catch (error) {
        if (error.name === 'AbortError' || request !== this.request) return;
        if (!this.rendered) { list.hidden = true; status.hidden = false; status.textContent = this.dataset.unavailable; }
      }
    }
  });
}

